import { LitElement, html, css } from 'lit-all'
import {
  t,
  StorefrontClient,
  // getStampedRatingData,
  renderStampedRatingBadge,
  refreshFocusTrap,
} from 'lib-utils'
import styles from 'lib-styles'

class NovaSearchResult extends LitElement {
  static properties = {
    noResults: { type: String },
    searchAgain: { type: String },
    searchData: { type: Object },
    searchTerm: { type: String },
    noSearchResult: { type: Boolean },
    suggestedResult: { type: Object },
    openState: { type: String },
    productData: { type: Array, state: true },
    _loading: { type: Boolean, state: true },
    productColours: { type: Object, state: true },
    displayProducts: { type: Array },
    ratingData: { type: Array },
  }

  constructor() {
    super()
    this.searchData = {}
    this.searchTerm = ''
    this.noSearchResult = false
    this.suggestedResult = null
    this.client = new StorefrontClient()
    this.productData = []
    this.productColours = {}
    this.displayProducts = []
    this.ratingData = []
  }

  connectedCallback() {
    super.connectedCallback()

    window.addEventListener('resize', this.handleRenderHeight.bind(this))
  }

  disconnectedCallback() {
    super.disconnectedCallback()

    window.removeEventListener('resize', this.handleRenderHeight.bind(this))
  }

  async _fetchData(productId) {
    return await this.client.getProductById(productId)
  }

  getColourSwatches(productId) {
    // returns array of colours
    return this.productColours[productId]
      ? this.productColours[productId]['coloursArray']
      : []
  }

  getSwatchFiles(productId) {
    // return object where key is colour name and value is colour file
    return this.productColours[productId]
      ? this.productColours[productId]['colourFiles']
      : {}
  }

  async loadColours(products) {
    const promises = products.map((product) => this._fetchData(product.id))
    const results = await Promise.all(promises)

    results.forEach((data) => {
      if (data != null) {
        let colourOptions = data.options.find(
          (option) =>
            option.name.toLowerCase() === 'color' ||
            option.name.toLowerCase() === 'colour'
        )
        let newObjEntry = {}

        let colourFiles = {}
        data.variants.forEach((variant) => {
          if (variant.selectedOptions && variant.selectedOptions.length > 0) {
            const colourOption = variant.selectedOptions.find(
              (option) =>
                option.name.toLowerCase() === 'color' ||
                option.name.toLowerCase() === 'colour'
            )
            if (colourOption) {
              const colorSwatchHandle = colourOption.value
                .toLowerCase()
                .replace(/ /g, '-')
              if (
                !variant.metafields['as-wfh'] ||
                !variant.metafields['as-wfh'].color_swatch_file
              )
                return
              const colorSwatchFile =
                variant.metafields['as-wfh']?.color_swatch_file.image.url
              if (colorSwatchFile)
                colourFiles[colorSwatchHandle] = colorSwatchFile
            }
          }
        })

        if (colourOptions != null) {
          newObjEntry[data.id] = {
            coloursArray: colourOptions.values,
            colourFiles: colourFiles,
          }
          this.productColours = { ...this.productColours, ...newObjEntry }
        }
      }
    })
  }

  async updated(changedProperties) {
    super.updated(changedProperties)
    if (this.openState === 'closed') return
    if (Reflect.ownKeys(this.searchData).length === 0) return

    // Debug: log received searchData structure

    const results = this.searchData?.resources?.results
    if (!results) {
      console.error("Search results missing or structure unexpected", this.searchData)
      return
    }
    const { products, collections, articles, pages } = results

    this.noSearchResult = [products, collections, articles, pages].every(
      (arr) => arr.length === 0
    )

    if (changedProperties.has('searchData') && products.length > 0) {
      this.loadColours(products)
      this.displayProducts = products.slice(0, 6)
      // this.ratingData = await getStampedRatingData(this.displayProducts)
    }

    if (this.suggestedResult === null && this.noSearchResult) {
      this.handleResult()
    } else if (this.noSearchResult) {
      this.handleRenderResult(this.suggestedResult)
    }

    this.handleRenderHeight()
    refreshFocusTrap(this, 0)
  }

  async handleResult() {
    try {
      const products = await this.client.searchProducts(this.searchTerm);
      // Build searchData object using Shopify API results
      console.debug('→ Shopify returned', products.length, 'products', products);
      this.searchData = {
        resources: {
          results: {
            products,
            collections: [],
            articles: [],
            pages: []
          }
        }
      };
      if (products.length > 0) {
        this.loadColours(products);
        this.displayProducts = products.slice(0, 6);
        // this.ratingData = await getStampedRatingData(this.displayProducts);
      }
    } catch (error) {

    }
  }

  handleRenderHeight() {
    if (this.openState === 'closed') return

    clearTimeout(this.debounceTimeout)
    this.debounceTimeout = setTimeout(() => {
      const resultRec = this.getBoundingClientRect()
      if (window.innerHeight - resultRec.bottom > 0) {
        this.style.maxHeight = 'unset'
      } else {
        this.style.maxHeight = `${window.innerHeight - resultRec.top}px`
      }
    }, 500)
  }

  handleRenderResult(data) {
    const suggestedResult = data.querySelector('.suggested-results')
    if (!suggestedResult) return

    const productContainer = suggestedResult.querySelector(
      '.header-search__product-container'
    )
    const currentProductContainer = this.shadowRoot.querySelector(
      '.header-search__product-container'
    )
    if (productContainer && currentProductContainer) {
      currentProductContainer.replaceWith(productContainer.cloneNode(true))
    }
  }

  render() {
    if (Reflect.ownKeys(this.searchData).length === 0) return html``
    if (this.noSearchResult) {
      return html`
        <div class="header-search__result">
          <div class="header-search__no-result-container">
            <div class="header-search__no-result-content">
              <h3 class="header-search__no-result-title">
                ${this.noResults} "${this.searchTerm}"
              </h3>
              <p class="header-search__no-result-description">
                ${this.searchAgain}
              </p>
            </div>
          </div>
          <div class="header-search__product-container"></div>
        </div>
      `
    }

    return html`
      <div class="header-search__result">
        ${this.displayProducts.length > 0
          ? html` <div class="header-search__product-container">
              <h3 tabindex="0" class="visually-hidden">
                ${t('sections.main-suggested-results.products')}
              </h3>
              <div class="header-search__product-inner-container">
                ${this.displayProducts.map(
                  (product) => html`
                    <nova-product-card
                      id="${product.id}"
                      handle="${product.handle}"
                      url="${product.url}"
                      available="${product.available}"
                      title="${product.title}"
                      image="${product.image}"
                      ${product.compare_at_price_max !== '0.00' &&
                      product.price < product.compare_at_price_max
                        ? html`compare-at-price=${product.compare_at_price}`
                        : null}
                      price="${product.price}"
                      vendor="${product.vendor}"
                      .swatches="${this.getColourSwatches(product.id)}"
                      .colourSwatchesFiles="${this.getSwatchFiles(product.id)}"
                      no-quick-buy="true"
                      msrp=""
                    >
   

                    </nova-product-card>
                  `
                )}
              </div>
            </div>`
          : html``}
      </div>
    `
  }

  static styles = [
    styles.wishListKing,
    styles.stamped,
    css`
      :host .header-search__result {
        display: flex;
        width: 100%;
        overflow: hidden;
        flex-direction: column;
        overflow-y: auto;
        -ms-overflow-style: none; /* Hide scrollbar for IE and Edge */
        scrollbar-width: none; /* Hide scrollbar for Firefox */
        max-height: var(--search-result-max-height, calc(100vh - 250px));
      }

      /* Hide scrollbar for Chrome, Safari and Opera */
      :host .header-search__result::-webkit-scrollbar {
        display: none;
        width: 0;
      }

      :host .header-search__product-container {
        margin: var(--nova-space-xl) 0 0 0;
        width: 100%;
      }

      :host .header-search__product-inner-container {
        display: flex;
        flex-direction: row;
        flex-wrap: wrap;
        width: 100%;
        gap: var(--nova-space-xs);
      }

      :host .header-search__product-inner-container > * {
        flex: 0 0 calc(50% - var(--nova-space-2xs));
      }

      :host h3 {
        font: var(--nova-h5-font);
        font-size: 20px;
        margin: 0 0 var(--nova-space-md);
      }

      @media screen and (min-width: 990px) {
        :host .header-search__product-inner-container {
          flex-wrap: nowrap;
          gap: var(--nova-space-xl);
        }

        :host .header-search__product-inner-container > * {
          flex: 0 0 15%;
        }

        :host h3 {
          font: var(--nova-h6-font);
        }
      }

      :host a {
        color: inherit;
        text-decoration: none;
      }

      :host ul {
        display: flex;
        flex-direction: column;
        gap: var(--nova-space-xs);
        padding-left: var(--nova-space-none);
        margin: var(--nova-space-none);
      }

      :host li {
        list-style: none;
        color: var(--nova-brand-neutral);
      }

      :host .header-search__no-result-title {
        font: var(--nova-h4-font);
        margin: var(--nova-space-2xl) 0 var(--nova-space-2xs);
      }

      :host .header-search__no-result-description {
        color: var(--nova-brand-subdued);
        margin: var(--nova-space-none);
      }

      :host .header-search__no-result-container {
        display: flex;
        flex-direction: column;
        gap: var(--nova-space-md);
      }

      /* the following styles are for the temperary product cards, can be removed once the custom product card component is ready */
      :host .header-search__product-card {
        width: 160px;
        padding-bottom: var(--nova-space-md);
      }

      :host .header-search__product-card img {
        width: 100%;
        aspect-ratio: 160 / 214;
        object-fit: cover;
        object-position: center;
      }

      :host h4 {
        font: var(--nova-h6-font);
        margin: var(--nova-space-none);
      }

      :host p {
        margin: var(--nova-space-none);
      }

      :host .header-search__product-compared-price {
        color: var(--nova-brand-neutral);
        text-decoration: line-through;
      }

      @media screen and (min-width: 1300px) {
        :host .header-search__product-card {
          width: 220px;
        }

        :host h4 {
          font-size: 18px;
        }
      }
    `,
  ]
}

customElements.define('nova-search-result', NovaSearchResult)
